import {
  Box,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  Typography,
} from "@mui/material";
import { Link, NavLink } from "react-router-dom";

import { useAppSelector } from "../../app/hooks";
import useProfile from "../../hook/useProfile";
import useWallet from "../../hook/useWallet";

import Logo from "../../components/Logo";

import { SideNavLinks } from "../../constants/page";

import config from "../../config/config";
import {
  convertDateTimeWithMinutes,
  formatNumberWithComma,
  shortAddress,
} from "../../utils/utils";

import { useStyles } from "./SideBar.styles";
import { useMemo } from "react";
import useCurrentTime from "../../hook/useCurrentTime";
import useGameBankBalance from "../../hook/useGameBankBalance";
import { City } from "../../constants/const";
import { JailWhiteListPath } from "../../constants/jail";

const MobileSideBar = ({
  showMobileSideBar,
  toggleMobileSideBar,
  onConnectWallet,
}: {
  showMobileSideBar: boolean;
  toggleMobileSideBar: React.Dispatch<React.SetStateAction<boolean>>;
  onConnectWallet: () => void;
}) => {
  const { classes } = useStyles();
  const currentTime = useCurrentTime();
  const { disconnectWallet, account, isConnected } = useWallet();
  const { profileName } = useProfile(account);
  const { userTravelInfo, helperCreditBalance, myProfile } = useAppSelector(
    (state) => state.profile
  );
  const { signMsg, signature } = useAppSelector((state) => state.auth);
  const { balance } = useGameBankBalance(account, signMsg, signature);

  const {
    nextCrimeTime,
    jailedUntil,
    nextNickTime,
    nextTrainTime,
    userCarBotInfo,
    userBoozeBotInfo,
    userCrimeBotInfo,
    userKSBotInfo,
    userNarcsBotInfo,
    userBulletBotInfo,
    nextNarcsTime,
    nextBoozeTime,
    nextBulletBuyTime,
  } = useAppSelector((state) => state.game);

  const remainTimes: Record<string, any[]> = useMemo(() => {
    return {
      Crimes: [
        nextCrimeTime > currentTime
          ? convertDateTimeWithMinutes(nextCrimeTime - currentTime)
          : "Now",
        userCrimeBotInfo.isRunning ? "Bot" : "",
      ],

      "Nick a car": [
        nextNickTime > currentTime
          ? convertDateTimeWithMinutes(nextNickTime - currentTime)
          : "Now",
        userCarBotInfo.isRunning ? "Bot" : "",
      ],

      Travel: [
        userTravelInfo
          ? userTravelInfo?.travelUntil > currentTime
            ? convertDateTimeWithMinutes(
                userTravelInfo?.travelUntil - currentTime
              )
            : "Now"
          : "Now",
      ],
      Jail: [
        jailedUntil > currentTime
          ? convertDateTimeWithMinutes(jailedUntil - currentTime)
          : "Now",
      ],
      "Helper bots": [""],
      "Kill skill": [
        nextTrainTime > currentTime
          ? convertDateTimeWithMinutes(nextTrainTime - currentTime)
          : "Now",
        userKSBotInfo.isRunning ? "Bot" : "",
      ],
      Narcotics: [
        nextNarcsTime > currentTime
          ? convertDateTimeWithMinutes(nextNarcsTime - currentTime)
          : "Now",
        userNarcsBotInfo.isRunning ? "Bot" : "",
      ],
      Booze: [
        nextBoozeTime > currentTime
          ? convertDateTimeWithMinutes(nextBoozeTime - currentTime)
          : "Now",
        userBoozeBotInfo.isRunning ? "Bot" : "",
      ],
      "Bullet Factory": [
        nextBulletBuyTime > currentTime
          ? convertDateTimeWithMinutes(nextBulletBuyTime - currentTime)
          : "Now",
        userBulletBotInfo.isRunning ? "Bot" : "",
      ],
    };
  }, [
    nextCrimeTime,
    currentTime,
    userCrimeBotInfo.isRunning,
    nextNickTime,
    userCarBotInfo.isRunning,
    userTravelInfo,
    jailedUntil,
    nextTrainTime,
    userKSBotInfo.isRunning,
    nextNarcsTime,
    userNarcsBotInfo.isRunning,
    nextBoozeTime,
    userBoozeBotInfo.isRunning,
    nextBulletBuyTime,
    userBulletBotInfo.isRunning,
  ]);

  const { isInJail } = useAppSelector((state) => state.profile);

  return (
    <Drawer
      PaperProps={{ className: classes.sidebarPaper }}
      open={showMobileSideBar}
      onClose={() => toggleMobileSideBar(false)}
    >
      <Box className={classes.sidebarHeader}>
        <Logo classNames={classes.sidebarLogo} />
        <NavLink to="/"> {config.title}</NavLink>
      </Box>

      <Box className={classes.navLinkGroup}>
        <Box className={classes.navLinkHeader}>
          <Box className={classes.navLinkHeaderContent}>
            <Box
              component={"img"}
              src={"/assets/imgs/layout/currency.png"}
              className={classes.navLinkIcon}
            />
            <Typography className={classes.navLinkText}>Cash</Typography>
          </Box>
          {formatNumberWithComma(Math.floor(balance))}
        </Box>
      </Box>
      <Box
        className={classes.navLinkGroup}
        sx={{ marginBottom: 2, marginTop: 0.5 }}
      >
        <Box className={classes.navLinkHeader}>
          <Box className={classes.navLinkHeaderContent}>
            <Box
              component={"img"}
              src={"/assets/imgs/inventory/helperitem.png"}
              className={classes.navLinkIcon}
            />
            <Typography className={classes.navLinkText}>Credits</Typography>
          </Box>
          {formatNumberWithComma(Math.floor(helperCreditBalance))}
        </Box>
      </Box>

      {SideNavLinks.map((navLinks, index) => {
        return (
          <Box className={classes.navLinkGroup} key={index}>
            <Box className={classes.navLinkHeader}>
              <Box className={classes.navLinkHeaderContent}>
                <Box
                  component={"img"}
                  src={navLinks.icon}
                  className={classes.navLinkIcon}
                />
                <Typography className={classes.navLinkText}>
                  {navLinks.category === "LOCATION"
                    ? myProfile.city !== undefined
                      ? City[myProfile.city]
                      : "LOCATION"
                    : navLinks.category}
                </Typography>
              </Box>

              <Box className={classes.navLinkHeaderLine}></Box>
            </Box>

            <Box className={classes.navLinks}>
              {navLinks.subLinks.map((link, linkIndex) => {
                return (
                  <Link
                    to={
                      isInJail && JailWhiteListPath.indexOf(link.link) === -1
                        ? "/injail"
                        : navLinks.category === "LOCATION"
                        ? link.link + `/${myProfile.city || 0}`
                        : link.link
                    }
                    className={classes.navLink}
                    key={linkIndex}
                  >
                    <Typography>{link.name}</Typography>

                    <Box className={classes.badgeList}>
                      {(link as any).showCooldown &&
                        remainTimes[link.name].map((time, index) => {
                          return time ? (
                            <Box
                              key={index}
                              className={
                                time === "Bot"
                                  ? classes.botBadge
                                  : time === "Now"
                                  ? classes.nowBadge
                                  : classes.timeBadge
                              }
                              sx={{
                                backgroundColor:
                                  link.name === "Jail" && time !== "Now"
                                    ? "rgba(255, 0, 64, 0.78)!important"
                                    : "",
                              }}
                            >
                              {time ?? "Now"}
                            </Box>
                          ) : (
                            ""
                          );
                        })}
                    </Box>
                  </Link>
                );
              })}
            </Box>
          </Box>
        );
      })}

      <Divider />
      <List>
        {isConnected && (
          <ListItem>
            <ListItemButton className={classes.sidebarNavLink}>
              <NavLink
                className={classes.sidebarNavLink}
                to={isInJail ? "/" : `/profile/${profileName}`}
              >
                <Typography component="span" sx={{ fontWeight: 700 }}>
                  {shortAddress(account)}
                </Typography>
                <Box
                  component="img"
                  width={18}
                  sx={{ marginLeft: "8px" }}
                  src="/assets/imgs/layout/user.png"
                ></Box>
              </NavLink>
            </ListItemButton>
          </ListItem>
        )}

        <ListItem>
          <ListItemButton
            className={classes.sideBarConnectWalletBtn}
            onClick={() =>
              isConnected ? disconnectWallet() : onConnectWallet()
            }
          >
            <Typography component="span" sx={{ fontWeight: 700 }}>
              {isConnected ? "Disconnect" : "Connect Wallet"}
            </Typography>
            {isConnected && (
              <Box
                component="img"
                width={18}
                sx={{ marginLeft: "8px" }}
                src="/assets/imgs/layout/power-off.png"
              ></Box>
            )}
          </ListItemButton>
        </ListItem>
      </List>
    </Drawer>
  );
};

export default MobileSideBar;
